/*
 * АНИМАЦИИ-ТЕОРИЯ для уроков «Кубикон CAD».
 *
 * Короткие SVG-мультики, которые показывают то, что словами объяснить трудно:
 * как эскиз превращается в объём, как выгрызается вырез, чем скругление
 * отличается от фаски, почему оболочка делает коробку за одну операцию.
 *
 * ПРОИСХОЖДЕНИЕ. Основа перенесена из Майнкрафтии 2.0
 * (frontend/src/lab/movie/cadDemos.tsx) — там эти анимации уже написаны для
 * мультиков-теории робототехники. Здесь они переведены с TypeScript на JSX
 * (редактор собирается CRA без TS) и перекрашены под тёмную тему редактора:
 * там подложка была светлая «инженерная», у нас панель тёмная, и светлый
 * прямоугольник в ней выглядел бы заплаткой. Добавлены сцены, которых в
 * исходнике не было, но которые нужны курсу 1: вырез, оболочка, размеры.
 *
 * ПОЧЕМУ SMIL, А НЕ JS. Анимации объявлены прямо в SVG (<animate>,
 * <animateTransform>). Им не нужен ни таймер, ни состояние компонента, ни
 * перерисовка React — браузер крутит их сам. Панель урока может открываться
 * и закрываться сколько угодно, утечь тут нечему.
 *
 * ДВИЖЕНИЕ. Кто выключил анимации в системе, не должен видеть мельтешение:
 * в CSS-модуле стоит правило prefers-reduced-motion, гасящее проигрывание.
 */

import React from 'react';
import UiDemo, { UI_DEMO_KINDS } from './UiDemos';
import cl from './CadDemos.module.css';


/* ── Изометрия: точка (x, y, z) пространства → точка экрана ──────────────
 * x — вправо-вглубь, y — влево-вглубь, z — вверх. Классические 30°.     */
const ISO = 0.5;   // вертикальное сжатие глубины

function iso(x, y, z) {
    return [(x - y) * 0.866, (x + y) * ISO - z];
}

/** Точка плоскости эскиза (z = 0) со смещением dx, dy — для контуров. */
function pt(cx, cy, x, y) {
    const [sx, sy] = iso(x, y, 0);
    return `${cx + sx},${cy + sy}`;
}

/*
 * Точка НА ГРАНИ тела высотой z — для выреза и полости.
 *
 * Разница с pt() существенная: pt кладёт точку на плоскость земли (z = 0),
 * и если ею рисовать вырез в верхней грани бруска, фигура повиснет в воздухе
 * над деталью. Здесь z учитывается, поэтому контур ложится ровно на грань.
 */
function ptZ(cx, cy, x, y, z) {
    const [sx, sy] = iso(x, y, z);
    return `${cx + sx},${cy + sy}`;
}

/** Четырёхугольник на грани высоты z: половины сторон hx, hy. */
function facePoly(cx, cy, hx, hy, z) {
    return `${ptZ(cx, cy, -hx, -hy, z)} ${ptZ(cx, cy, hx, -hy, z)} `
        + `${ptZ(cx, cy, hx, hy, z)} ${ptZ(cx, cy, -hx, hy, z)}`;
}


/*
 * Заливки граней. Три оттенка одного цвета дают ощущение объёма:
 * верх светлее, бока темнее. Цвет — латунно-песочный: на тёмном фоне
 * читается как металл и не спорит с циановым акцентом интерфейса.
 */
function Defs() {
    return (
        <defs>
            <linearGradient id="kcd-top" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#e8b877"/><stop offset="1" stopColor="#d9a25c"/>
            </linearGradient>
            <linearGradient id="kcd-left" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#b5813f"/><stop offset="1" stopColor="#9a6c31"/>
            </linearGradient>
            <linearGradient id="kcd-right" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#c99150"/><stop offset="1" stopColor="#a97a3c"/>
            </linearGradient>
        </defs>
    );
}

/** Брусок w × d × h с центром в (cx, cy) — три видимые грани. */
function Box({ cx, cy, w, d, h }) {
    const P = (x, y, z) => {
        const [sx, sy] = iso(x, y, z);
        return `${cx + sx},${cy + sy}`;
    };
    const x0 = -w / 2, x1 = w / 2, y0 = -d / 2, y1 = d / 2, z0 = 0, z1 = h;
    return (
        <g stroke="#6b4a1f" strokeWidth="0.5" strokeLinejoin="round">
            <polygon points={`${P(x0, y0, z1)} ${P(x1, y0, z1)} ${P(x1, y1, z1)} ${P(x0, y1, z1)}`}
                fill="url(#kcd-top)"/>
            <polygon points={`${P(x0, y1, z1)} ${P(x1, y1, z1)} ${P(x1, y1, z0)} ${P(x0, y1, z0)}`}
                fill="url(#kcd-left)"/>
            <polygon points={`${P(x1, y0, z1)} ${P(x1, y1, z1)} ${P(x1, y1, z0)} ${P(x1, y0, z0)}`}
                fill="url(#kcd-right)"/>
        </g>
    );
}

/** Цилиндр радиуса r высотой h — эллипс сверху и боковая поверхность. */
function Cyl({ cx, cy, r, h, topFill = 'url(#kcd-top)' }) {
    const rx = r * 0.866, ry = r * ISO;
    return (
        <g stroke="#6b4a1f" strokeWidth="0.5">
            <path d={`M${cx - rx},${cy - h} A${rx},${ry} 0 0 0 ${cx + rx},${cy - h} `
                + `L${cx + rx},${cy} A${rx},${ry} 0 0 1 ${cx - rx},${cy} Z`}
                fill="url(#kcd-right)"/>
            <ellipse cx={cx} cy={cy - h} rx={rx} ry={ry} fill={topFill}/>
        </g>
    );
}

/*
 * Общая обёртка сцены: кадр и подпись под ним.
 *
 * Уважаем системную настройку «меньше движения». SMIL-анимации не подчиняются
 * CSS, поэтому останавливаем их единственным способом, который работает, —
 * методом pauseAnimations() у самого svg-элемента. Кадр при этом остаётся
 * видимым: анимация замирает на первом кадре, а не исчезает.
 */
function Scene({ caption, children, bare }) {
    const svgRef = React.useRef(null);

    React.useEffect(() => {
        const svg = svgRef.current;
        if (!svg || typeof svg.pauseAnimations !== 'function') return;
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => {
            if (media.matches) svg.pauseAnimations();
            else svg.unpauseAnimations();
        };
        apply();
        media.addEventListener('change', apply);
        return () => media.removeEventListener('change', apply);
    }, []);

    return (
        /* bare — режим для модалки теории: там кадр во весь экран и свой
           титр под ним, поэтому подпись и рамка сцены не нужны. */
        <figure className={bare ? cl.wrapBare : cl.wrap}>
            <svg ref={svgRef} viewBox="0 0 100 56"
                className={bare ? cl.svgBare : cl.svg}
                preserveAspectRatio="xMidYMid meet" role="img" aria-label={caption}>
                <Defs/>
                {children}
            </svg>
            {!bare && <figcaption className={cl.caption}>{caption}</figcaption>}
        </figure>
    );
}


/* ═══ ВЫДАВЛИВАНИЕ: эскиз поднимается в объём ═══════════════════════════ */
function Extrude({ shape, bare }) {
    const circle = shape === 'circle';
    return (
        <Scene bare={bare} caption="Выдавливание: плоский эскиз поднимается и становится объёмной деталью">
            {/* Контур эскиза остаётся на месте — видно, откуда выросло тело. */}
            <g opacity="0.9">
                {circle
                    ? <ellipse cx="50" cy="40" rx="13" ry="6.5" fill="none"
                        stroke="#4d9de0" strokeWidth="1" strokeDasharray="2 1.5"/>
                    : <polygon fill="none" stroke="#4d9de0" strokeWidth="1" strokeDasharray="2 1.5"
                        points={`${pt(50, 40, -15, -8)} ${pt(50, 40, 15, -8)} `
                            + `${pt(50, 40, 15, 8)} ${pt(50, 40, -15, 8)}`}/>}
            </g>
            <g>
                <animateTransform attributeName="transform" type="scale"
                    values="1 0.05; 1 1; 1 1; 1 0.05" keyTimes="0;0.4;0.75;1"
                    dur="3.4s" repeatCount="indefinite" additive="sum"/>
                {circle ? <Cyl cx={50} cy={40} r={15} h={16}/>
                    : <Box cx={50} cy={40} w={30} d={16} h={16}/>}
            </g>
        </Scene>
    );
}


/* ═══ ВЫРЕЗ: та же операция, но материал убирается ══════════════════════ */
function Cut({ bare }) {
    return (
        <Scene bare={bare} caption="Вырез — то же выдавливание, только внутрь: фигура прогрызает материал">
            <Box cx={50} cy={38} w={40} d={22} h={14}/>
            {/*
             * Углубление в верхней грани. Рисуем два контура: тёмное дно
             * (лежит ниже грани — на z=8) и светлый контур самого выреза на
             * грани (z=14). Дно проявляется постепенно — как будто фигура
             * прогрызает материал вглубь.
             */}
            <g opacity="0">
                <animate attributeName="opacity" values="0;1;1;0"
                    keyTimes="0;0.4;0.8;1" dur="3.4s" repeatCount="indefinite"/>
                <polygon fill="#2a2018" stroke="#1a130c" strokeWidth="0.4"
                    points={facePoly(50, 38, 11, 6, 8)}/>
                <polygon fill="none" stroke="#3ecf8e" strokeWidth="0.7"
                    points={facePoly(50, 38, 11, 6, 14)}/>
            </g>
        </Scene>
    );
}


/* ═══ ОТВЕРСТИЯ: сверлим сквозь деталь ═════════════════════════════════ */
function Hole({ count = 1, bare }) {
    const n = Math.max(1, Math.min(4, count));
    /*
     * Центры отверстий задаём В КООРДИНАТАХ ДЕТАЛИ (x вдоль, y поперёк),
     * а на экран переводим изометрией — тогда они ложатся на грань там, где
     * должны. Раньше x пересчитывался «на глаз» делением, и четыре отверстия
     * уезжали по диагонали, а крайнее вылезало за грань.
     */
    const spots = n === 1 ? [[0, 0]]
        : n === 2 ? [[-11, 0], [11, 0]]
            : n === 3 ? [[-12, 0], [0, 0], [12, 0]]
                // Четыре по углам. Разводим по y сильнее, чем по x: в изометрии
                // смещение по глубине даёт и горизонтальный сдвиг, и при
                // одинаковом разбросе углы визуально схлопываются в диагональ.
                : [[-13, -7], [13, -7], [-13, 7], [13, 7]];
    return (
        <Scene bare={bare} caption={n === 1
            ? 'Отверстие под винт: сверлим деталь насквозь'
            : `${n} отверстия под крепёж — обычно их делают одним эскизом`}>
            <Box cx={50} cy={38} w={36} d={22} h={12}/>
            {spots.map(([x, y], i) => {
                const [sx, sy] = iso(x, y, 12);
                return (
                    <ellipse key={i} cx={50 + sx} cy={40 + sy} rx="2.8" ry="1.6"
                        fill="#2a2018" stroke="#1a130c" strokeWidth="0.4">
                        {/* Сверло опускается — отверстие «раскрывается». */}
                        <animate attributeName="ry" values="0.2;1.6;1.6"
                            keyTimes="0;0.4;1" dur="2.6s"
                            repeatCount="indefinite" begin={`${i * 0.3}s`}/>
                    </ellipse>
                );
            })}
        </Scene>
    );
}


/* ═══ СКРУГЛЕНИЕ И ФАСКА: сравнение с острым углом ═════════════════════ */
function EdgeDemo({ mode, bare }) {
    const fillet = mode === 'fillet';
    const [ex, ey] = iso(10, -7, 14);
    return (
        <Scene bare={bare} caption={fillet
            ? 'Скругление убирает острый угол: нагрузка расходится по дуге, деталь прочнее'
            : 'Фаска — срезанный край: деталь легче входит в отверстие и не режет руки'}>
            <g>
                <Box cx={28} cy={40} w={20} d={14} h={14}/>
                <text x="28" y="52" textAnchor="middle" fontSize="3.6"
                    fill="#8b93a5">острый угол</text>
            </g>
            <g>
                <Box cx={70} cy={40} w={20} d={14} h={14}/>
                {fillet
                    ? <path d={`M${70 + ex},${40 + ey} q-4,-3 -4,3`}
                        fill="none" stroke="#3ecf8e" strokeWidth="1.4"/>
                    : <line x1={70 + ex - 4} y1={40 + ey}
                        x2={70 + ex} y2={40 + ey + 4}
                        stroke="#3ecf8e" strokeWidth="1.4"/>}
                <text x="70" y="52" textAnchor="middle" fontSize="3.6"
                    fill="#3ecf8e">{fillet ? 'скругление' : 'фаска'}</text>
            </g>
        </Scene>
    );
}


/* ═══ ОБОЛОЧКА: из бруска — коробка ════════════════════════════════════ */
function Shell({ bare }) {
    return (
        <Scene bare={bare} caption="Оболочка выбирает грань и делает деталь полой: стенки одной толщины">
            <Box cx={50} cy={38} w={36} d={22} h={18}/>
            {/*
             * Полость раскрывается сверху — верхнюю грань как будто убрали.
             * Внутренний контур меньше наружного ровно на толщину стенки:
             * по нему и видно, что стенки везде одинаковые.
             */}
            <g opacity="0">
                <animate attributeName="opacity" values="0;0;1;1"
                    keyTimes="0;0.3;0.55;1" dur="3.6s" repeatCount="indefinite"/>
                {/* Дно полости — ниже верхней грани на глубину коробки. */}
                <polygon fill="#2a2018" stroke="#1a130c" strokeWidth="0.4"
                    points={facePoly(50, 38, 15, 8, 4)}/>
                {/* Кромка стенки на верхней грани. */}
                <polygon fill="none" stroke="#3ecf8e" strokeWidth="0.8"
                    points={facePoly(50, 38, 15, 8, 18)}/>
            </g>
        </Scene>
    );
}


/* ═══ РАЗМЕРЫ: почему «на глаз» плохо ═════════════════════════════════ */
function Dimensions({ bare }) {
    return (
        <Scene bare={bare} caption="Размер держит эскиз: без него фигура поедет при первой же правке">
            {/*
             * Слева — эскиз без размеров: он «дышит», его ведёт при каждой
             * правке. Масштабируем вокруг центра прямоугольника, иначе фигура
             * не пульсирует, а уползает вбок.
             */}
            <g transform="translate(26 26)">
                <g>
                    <animateTransform attributeName="transform" type="scale"
                        values="1 1; 1.16 0.88; 0.9 1.1; 1 1" dur="3s"
                        repeatCount="indefinite"/>
                    <rect x="-12" y="-8" width="24" height="16" rx="0.5" fill="none"
                        stroke="#f4666f" strokeWidth="1.1" strokeDasharray="2 1.5"/>
                </g>
            </g>
            <text x="26" y="49" textAnchor="middle" fontSize="3.8"
                fill="#f4666f">без размеров — едет</text>

            {/* Справа — тот же прямоугольник, закреплённый числом. */}
            <g>
                <rect x="62" y="18" width="24" height="16" rx="0.5" fill="none"
                    stroke="#38d9f0" strokeWidth="1.1"/>
                {/* Размерная линия ПОД фигурой — как на настоящем чертеже.
                    Цифру сажаем НАД линией, но ниже контура: между ними
                    оставлен зазор, иначе она ложится на грань. */}
                <line x1="62" y1="41.5" x2="86" y2="41.5" stroke="#38d9f0" strokeWidth="0.5"/>
                <line x1="62" y1="35" x2="62" y2="43" stroke="#38d9f0"
                    strokeWidth="0.35" opacity="0.6"/>
                <line x1="86" y1="35" x2="86" y2="43" stroke="#38d9f0"
                    strokeWidth="0.35" opacity="0.6"/>
                <text x="74" y="39.9" textAnchor="middle" fontSize="3.8"
                    fill="#38d9f0" fontFamily="monospace">50</text>
                <text x="74" y="49" textAnchor="middle" fontSize="3.8"
                    fill="#38d9f0">с размерами — стоит</text>
            </g>
        </Scene>
    );
}


/* ═══ ТЕЛО ВРАЩЕНИЯ ═══════════════════════════════════════════════════ */
function Revolve({ bare }) {
    return (
        <Scene bare={bare} caption="Вращение: профиль крутится вокруг оси и даёт вал, воронку или вазу">
            <line x1="50" y1="8" x2="50" y2="50" stroke="#4d9de0"
                strokeWidth="0.5" strokeDasharray="2 1.5"/>
            <polyline points="50,12 44,12 44,24 40,24 40,40 46,40 46,48 50,48"
                fill="none" stroke="#e0602f" strokeWidth="1"/>
            {/* Вал появляется из профиля. Цикл начинаем с ПОЛНОЙ видимости:
                иначе первый кадр (и статичный скриншот) показывает деталь
                полупрозрачной, будто она бракованная. */}
            <g>
                <animate attributeName="opacity" values="1;0.25;1"
                    keyTimes="0;0.35;1" dur="3s" repeatCount="indefinite"/>
                <Cyl cx={68} cy={48} r={8} h={8}/>
                <Cyl cx={68} cy={40} r={12} h={16}/>
                <Cyl cx={68} cy={24} r={7} h={12}/>
            </g>
        </Scene>
    );
}


/* ═══ СБОРКА: крышка садится на корпус ════════════════════════════════ */
function Assembly({ bare }) {
    return (
        <Scene bare={bare} caption="Сборка: крышка садится на корпус — между ними нужен зазор">
            <Box cx={50} cy={46} w={30} d={18} h={10}/>
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="0,-14; 0,0; 0,0; 0,-14" keyTimes="0;0.4;0.75;1"
                    dur="3.4s" repeatCount="indefinite"/>
                <Box cx={50} cy={34} w={30} d={18} h={6}/>
            </g>
        </Scene>
    );
}


/* ═══ 3D-ПЕЧАТЬ: слой за слоем ════════════════════════════════════════ */
function Printer({ bare }) {
    const layers = 7;
    return (
        <Scene bare={bare} caption="3D-принтер печатает деталь слой за слоем — снизу вверх">
            {/* Стол принтера. */}
            <line x1="22" y1="48" x2="78" y2="48" stroke="#8b93a5" strokeWidth="1.2"/>
            {/*
             * Слои ложатся снизу вверх. Первый слой видно сразу (opacity="1"):
             * пустой стол в первом кадре читается как «ничего не работает».
             */}
            {Array.from({ length: layers }, (_, i) => (
                <rect key={i} x="36" y={44.4 - i * 4.2} width="28" height="3.6" rx="0.4"
                    fill="url(#kcd-top)" stroke="#9a6c31" strokeWidth="0.3"
                    opacity={i === 0 ? 1 : 0}>
                    <animate attributeName="opacity" values="0;1;1" keyTimes="0;0.05;1"
                        dur="4s" begin={`${i * 0.5}s`} repeatCount="indefinite"/>
                </rect>
            ))}
            {/* Печатающая головка ездит над деталью. */}
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="-13,0; 13,0; -13,0" dur="1.6s" repeatCount="indefinite"/>
                <rect x="43" y="9" width="14" height="7" rx="1.2" fill="url(#kcd-left)"
                    stroke="#6b4a1f" strokeWidth="0.4"/>
                <path d="M50,16 l-2.4,4 l4.8,0 z" fill="#8b93a5"/>
            </g>
        </Scene>
    );
}


/* ═══ ВИДЫ: как деталь смотрят с трёх сторон ══════════════════════════ */
function Views({ bare }) {
    return (
        <Scene bare={bare} caption="Три взгляда на одну деталь: спереди, сбоку и сверху — это и есть чертёж">
            {/* Деталь в объёме — слева. */}
            <Box cx={26} cy={38} w={22} d={14} h={12}/>
            {/*
             * Три вида справа. У каждого — не пустая рамка, а реальная
             * проекция того же бруска: спереди длина × высота, слева
             * глубина × высота, сверху длина × глубина. Пустые прямоугольники
             * ничего не объясняли — ребёнок должен увидеть, что виды разные
             * и каждый показывает свои две стороны детали.
             */}
            {[
                { y: 16, label: 'спереди', w: 15, h: 7 },
                { y: 30, label: 'слева', w: 9, h: 7 },
                { y: 44, label: 'сверху', w: 15, h: 9 },
            ].map(({ y, label, w, h }, i) => (
                <g key={label}>
                    <line x1="42" y1="38" x2={61} y2={y}
                        stroke="#38d9f0" strokeWidth="0.4" strokeDasharray="1.5 1">
                        <animate attributeName="opacity" values="0.25;1;0.25"
                            dur="3s" begin={`${i * 0.9}s`} repeatCount="indefinite"/>
                    </line>
                    <rect x={62} y={y - h / 2} width={w} height={h} rx="0.4"
                        fill="#2a2018" stroke="#eef1f6" strokeWidth="0.8"/>
                    <text x={62 + w + 3} y={y + 1.3} fontSize="3.6"
                        fill="#b9c0cf">{label}</text>
                </g>
            ))}
        </Scene>
    );
}


/** Виды анимаций, доступные урокам. */
export const DEMO_KINDS = new Set([
    'extrude', 'cut', 'hole', 'fillet', 'chamfer', 'shell',
    'dimensions', 'revolve', 'assembly', 'printer', 'views',
]);

/**
 * Показать анимацию по её виду.
 * @param {string} kind — из DEMO_KINDS
 * @param {Object} opts — параметры сцены (например, count для отверстий)
 */
const CadDemo = ({ kind, bare, ...opts }) => {
    /* Сцены с интерфейсом редактора живут в UiDemos — уроку не нужно
       различать два набора, он просто указывает kind. */
    if (UI_DEMO_KINDS.has(kind)) return <UiDemo kind={kind} bare={bare}/>;

    switch (kind) {
        case 'extrude':    return <Extrude shape={opts.shape} bare={bare}/>;
        case 'cut':        return <Cut bare={bare}/>;
        case 'hole':       return <Hole count={opts.count} bare={bare}/>;
        case 'fillet':     return <EdgeDemo mode="fillet" bare={bare}/>;
        case 'chamfer':    return <EdgeDemo mode="chamfer" bare={bare}/>;
        case 'shell':      return <Shell bare={bare}/>;
        case 'dimensions': return <Dimensions bare={bare}/>;
        case 'revolve':    return <Revolve bare={bare}/>;
        case 'assembly':   return <Assembly bare={bare}/>;
        case 'printer':    return <Printer bare={bare}/>;
        case 'views':      return <Views bare={bare}/>;
        default:           return null;
    }
};

export default CadDemo;
